/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, usePathname, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { Community } from "@packages/sdk";
import {
  getCommunityMemberCount,
  useActiveSidebarTab,
  useBranch,
} from "@packages/sdk";
import type { CSSColorStrict } from "@packages/ui";
import {
  AvatarCommunity,
  cssMediaQueryMinWidthTablet,
  FriendsIcon,
  QuickAction,
  ShareIcon,
  ShareModal,
  spacing,
  Tag,
  Text,
  useIsDesktopViewport,
  useModal,
} from "@packages/ui";
import { useParams } from "next/navigation";

import { getBasePADUrl, padTabsPaths } from "../../../../lib";

const containerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "center",
  margin: "auto",
  maxWidth: "600px",
  width: spacing.full,
};

const actionsContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "row",
  gap: spacing.l,
};

const descriptionStyles: Interpolation = {
  maxWidth: "100%",
};

const groupInfoTextContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  textAlign: "center",
};

const groupInfoDescriptionDetailsContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  maxWidth: "100%",
  textAlign: "center",
};

const shareModalImageStyles: Interpolation = {
  height: "250px",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "250px",
};

const shareModalContentContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  marginLeft: spacing.m,
  marginRight: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    marginLeft: "16%",
    marginRight: "16%",
  },
};

const tagStyles: Interpolation = {
  width: "fit-content !important",
};

const shareModalAvatarCommunityMobileStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  height: "50vh",
  justifyContent: "center",
};

type CommunityDetailsProps = {
  community: Community;
};

export const CommunityDetails = ({ community }: CommunityDetailsProps) => {
  const t = useTranslations();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const analytics = useAnalytics();
  const pathname = usePathname();
  const { communityId } = useParams<{ communityId: string }>();
  const isDesktop = useIsDesktopViewport();

  const activeSidebarTab = useActiveSidebarTab({
    baseUrl: getBasePADUrl({
      communityId: parseInt(communityId),
    }),
    tabs: padTabsPaths,
  });

  return (
    <div css={containerStyles}>
      {community.image_url && (
        <AvatarCommunity
          size="xl"
          color={community.color_hex as CSSColorStrict}
          imgSrc={community.image_url}
        />
      )}
      <div css={groupInfoTextContainerStyles}>
        <Text type="title" size="xl" as="h1">
          {community.name}
        </Text>
        {community.tag_text && (
          <Tag css={tagStyles} variant="neutral">
            {community.tag_text}
          </Tag>
        )}
      </div>
      <div css={groupInfoDescriptionDetailsContainerStyles}>
        {community.description && (
          <Text type="body" size="m" as="p" css={descriptionStyles}>
            {community.description}
          </Text>
        )}
      </div>
      <div css={actionsContainerStyles}>
        <QuickAction key="quickActionMembers" icon={<FriendsIcon />} asChild>
          <Link
            href={`${pathname}${pathname.endsWith("/") ? "" : "/"}members`}
            onClick={() =>
              analytics.track({
                event: HallowAnalyticsEvent.TappedGroupMembers,
                properties: {
                  community_id: community.id,
                  location: activeSidebarTab ? `${activeSidebarTab}_tab` : "",
                },
              })
            }
          >
            {t("community_settings_members")}
          </Link>
        </QuickAction>
        <QuickAction
          key="quickActionShare"
          icon={<ShareIcon />}
          onClick={async () => {
            shareModal.show({
              color: community.color_hex,
              children: (
                <AvatarCommunity
                  color={community.color_hex as CSSColorStrict}
                  description={community.simple_address_label}
                  name={community.name}
                  imgSrc={community.image_url}
                  size={isDesktop ? "responsive" : "xxl"}
                  topLeftImgSrc={community.member_summary.image_urls?.[0]}
                  bottomRightImgSrc={community.member_summary.image_urls?.[1]}
                  sublabel={t("plural_members", {
                    plural_members: getCommunityMemberCount({ community }),
                  })}
                  css={!isDesktop && shareModalAvatarCommunityMobileStyles}
                />
              ),
              imgSrc: community.image_url,
              title: community.name,
              description: community.simple_address_label,
              imgCSS: [shareModalImageStyles],
              contentContainerCSS: [shareModalContentContainerStyles],
              link: await branch.community({
                id: community.id,
                name: community.name,
              }),
            });
            analytics.track({
              event: HallowAnalyticsEvent.TappedGroupInvite,
              properties: {
                community_id: community.id,
                location: activeSidebarTab ? `${activeSidebarTab}_tab` : "",
              },
            });
          }}
        >
          {t("manage_family_invite_member")}
        </QuickAction>
      </div>
    </div>
  );
};
